import type { IconName } from "components/Icon";

import { Stack } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { TransientResultFile } from "../lib/formatJobResults";

import { useInputSelection } from "../hooks/useInputSelection";
import { useResultZipDownload } from "../hooks/useResultZipDownload";
import { useTransientJobInputs } from "../hooks/useTransientJobInputs";
import { resultFileKey } from "../lib/formatJobResults";
import { formatInputFolderLabel, groupInputFiles, OTHER_FILES_GROUP } from "../lib/groupInputFiles";
import { isXlsxFileName } from "../lib/storedDirectory";
import { TransientJobDownloadModal } from "./TransientJobDownloadModal";
import { TransientJobInputToolbar } from "./TransientJobInputToolbar";
import { TransientJobResultRoomList } from "./TransientJobResultRoomList";

type TransientJobInputsProps = {
  configId?: number;
  shouldRefresh: boolean;
};

const JSON_ICON_COLOR = "var(--color-purpose-certificate-gold)";
const XLSX_ICON_COLOR = "var(--color-purpose-status-positive)";

const inputFileIcon = (file: TransientResultFile): { color: string; iconName: IconName } =>
  isXlsxFileName(file.name)
    ? { color: XLSX_ICON_COLOR, iconName: "Xlsx" }
    : { color: JSON_ICON_COLOR, iconName: "Json" };

export const TransientJobInputs = ({ configId, shouldRefresh }: TransientJobInputsProps) => {
  const { t } = useTranslation();
  const { error, files, isLoading } = useTransientJobInputs(configId, shouldRefresh);
  const groups = groupInputFiles(files);
  const { isAllSelected, isSelected, selectedFiles, setMany, toggle, toggleAll } = useInputSelection(groups);
  const download = useResultZipDownload();
  const [search, setSearch] = useState("");

  if (isLoading) {
    return <p className="text-lds-foreground-muted font-ds-body-regular-md">{t("general.loadingDataPleaseWait")}</p>;
  }
  if (error) {
    return <p className="text-lds-purpose-status-negative-subtle">{error}</p>;
  }
  if (files.length === 0) {
    return (
      <p className="text-lds-foreground-muted font-ds-body-regular-md">
        {t("Execution.transient.jobDetails.inputsEmpty")}
      </p>
    );
  }

  return (
    <div>
      <TransientJobInputToolbar
        allSelected={isAllSelected}
        disabled={selectedFiles.length === 0}
        onDownload={() => download.start(selectedFiles, "inputs", "inputs.zip")}
        onSearch={setSearch}
        onToggleAll={toggleAll}
        search={search}
      />
      <div className="grid grid-cols-3 gap-4">
        {groups.map((group) => {
          const folderName =
            group.folderName === OTHER_FILES_GROUP
              ? t("Execution.transient.jobDetails.otherFiles")
              : formatInputFolderLabel(group.folderName) || t("Execution.transient.jobDetails.inputFiles");
          return (
            <div className="flex min-h-0 flex-col" key={group.folderName || "inputs"}>
              <Stack className="mb-2 shrink-0 gap-2">
                <span className="font-ds-headline-sm truncate text-lds-foreground-heading-initial">{folderName}</span>
                <span className="text-lds-foreground-muted font-ds-body-regular-md">
                  {t("Execution.transient.jobDetails.fileCount", { count: group.files.length })}
                </span>
              </Stack>
              <TransientJobResultRoomList
                className="flex h-[28rem] flex-col"
                emptyLabel={t("Execution.transient.jobDetails.noMatchingFiles") ?? ""}
                fileIcon={inputFileIcon}
                fileKey={(file) => resultFileKey(group.folderName || "inputs", file.path || file.name)}
                files={group.files}
                folderName={group.folderName || "inputs"}
                isSelected={isSelected}
                listClassName="min-h-0 flex-1 overflow-auto"
                onSetMany={setMany}
                onToggle={toggle}
                search={search}
              />
            </div>
          );
        })}
      </div>
      <TransientJobDownloadModal
        error={download.error}
        isRunning={download.isRunning}
        onClose={download.close}
        open={download.open}
        progress={download.progress}
      />
    </div>
  );
};
